import { Injectable } from '@angular/core';
import { VehicleData } from '../../../shared/types';
import {
  CheckoutPaymentMethodCode,
  PaymentMethodCode,
} from '../../../shared/types/payment-method.enum';

export type WashOptionType = 'single' | 'monthly';
export type PaymentMethod = CheckoutPaymentMethodCode;

export interface WashTierOption {
  id: string;
  label: string;
  type: WashOptionType;
  family_plan?: boolean;
  priceLabel: string;
  originalPriceLabel?: string;
  /** Shown under option title, e.g. "2 benefits • 3 washes" (split on • in package card). */
  description?: string;
  /** Extra copy below the row (e.g. membership renewal disclaimer). */
  note?: string;
  price?: number;
  [key: string]: any;
}

export interface WashTier {
  id: string;
  name: string;
  features: string[];
  options: WashTierOption[];
}

export interface CustomerFormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  location: string;
}

export interface PaymentFormData {
  method: PaymentMethod;
  phoneNumber: string;
  cardholderName: string;
  billingAddress: string;
}

export interface FamilyVehicleSlot {
  id: string | number;
  state: string;
  licensePlate: string;
  vehicleData: VehicleData | null;
  errorMessage: string;
  isLoading: boolean;
  bookForWash: boolean;
}

export interface VehicleFormSlot {
  id: number;
  formData: {
    selectedState: string;
    licensePlate: string;
    vehicleId: string;
    vehicleData?: VehicleData | null;
    isVehicleFetched?: boolean;
    [key: string]: any;
  };
  vehicleData: VehicleData | null;
  isValid: boolean;
  isLoading: boolean;
  errorMessage: string;
  bookForWash: boolean;
}

export interface PriceBreakdown {
  subtotal: number;
  tax: number;
  total: number;
  label: string;
}

/**
 * Centralized state service for the Add Order Modal workflow.
 * Provided at the component level so state is scoped to each modal instance.
 * All four step-components read and write through this service to share state
 * without passing large @Input/@Output chains.
 */
@Injectable()
export class AddOrderStateService {
  // ── Package selection ──────────────────────────────────────────────────────
  washTiers: WashTier[] = [];
  selectedTierId = '';
  selectedOptionId = '';
  previousSelectedOptionId = '';
  isLoadingPackages = false;

  // ── Customer ───────────────────────────────────────────────────────────────
  customerForm: CustomerFormData = this.emptyCustomerForm();
  customerId = '';
  selectedExistingCustomer: any = null;

  // ── Location ───────────────────────────────────────────────────────────────
  selectedLocationId = '';
  selectedLocationName = '';
  availableLocations: any[] = [];

  // ── Vehicles ───────────────────────────────────────────────────────────────
  familyVehicleSlots: FamilyVehicleSlot[] = [];
  vehicleFormSlots: VehicleFormSlot[] = [];
  hasExistingVehicles = false;
  isFamilyPlanEnabled = false;
  hasSwitchedToFamilyPlan = false;
  familyPlanVehicleCount = 0;

  // ── Payment & order ────────────────────────────────────────────────────────
  paymentForm: PaymentFormData = this.emptyPaymentForm();
  createdOrderId = '';
  createdPaymentIntentId = '';
  createdOrderResponse: any = null;
  connectedStripeAccountId = '';

  // ── Derived getters ────────────────────────────────────────────────────────
  get selectedPackageOption(): WashTierOption | null {
    if (!this.selectedTierId || !this.selectedOptionId) return null;
    const tier = this.washTiers.find(t => t.id === this.selectedTierId);
    return tier?.options.find(o => o.id === this.selectedOptionId) ?? null;
  }

  get isSelectedPackageFamilyPlan(): boolean {
    return this.selectedPackageOption?.family_plan === true || this.hasSwitchedToFamilyPlan;
  }

  get isMonthlyMembership(): boolean {
    return this.selectedPackageOption?.type === 'monthly';
  }

  get hasMultipleVehicles(): boolean {
    return this.vehicleFormSlots.length + this.familyVehicleSlots.length > 1;
  }

  /** Returns the first vehicle that is marked for washing. */
  get selectedVehicle(): VehicleData | null {
    const fromFamily = this.familyVehicleSlots.find(s => s.bookForWash);
    if (fromFamily?.vehicleData) return fromFamily.vehicleData;
    const fromForm = this.vehicleFormSlots.find(s => s.bookForWash && s.vehicleData);
    return fromForm?.vehicleData ?? null;
  }

  // ── Factory helpers ────────────────────────────────────────────────────────
  emptyCustomerForm(): CustomerFormData {
    return { firstName: '', lastName: '', phone: '', email: '', location: '' };
  }

  emptyPaymentForm(method: PaymentMethod = PaymentMethodCode.Cash): PaymentFormData {
    return { method, phoneNumber: '', cardholderName: '', billingAddress: '' };
  }

  emptyVehicleFormSlot(id: number, bookForWash = false): VehicleFormSlot {
    return {
      id,
      formData: { selectedState: '', licensePlate: '', vehicleId: '' },
      vehicleData: null,
      isValid: false,
      isLoading: false,
      errorMessage: '',
      bookForWash,
    };
  }

  // ── Reset ──────────────────────────────────────────────────────────────────
  /**
   * Resets the add-order state to its initial values and initializes the payment form with the provided payment method.
   * @example
   * eset('cash')
   * void
   * @param {PaymentMethod} defaultPaymentMethod - The payment method used to initialize the payment form.
   * @returns {void} This function does not return a value.
   **/
  reset(defaultPaymentMethod: PaymentMethod = PaymentMethodCode.Cash): void {
    this.selectedTierId = '';
    this.selectedOptionId = '';
    this.previousSelectedOptionId = '';
    this.isLoadingPackages = false;

    this.customerForm = this.emptyCustomerForm();
    this.customerId = '';
    this.selectedExistingCustomer = null;

    this.selectedLocationId = '';
    this.selectedLocationName = '';
    this.availableLocations = [];

    this.familyVehicleSlots = [];
    this.vehicleFormSlots = [];
    this.hasExistingVehicles = false;
    this.isFamilyPlanEnabled = false;
    this.hasSwitchedToFamilyPlan = false;
    this.familyPlanVehicleCount = 0;

    this.paymentForm = this.emptyPaymentForm(defaultPaymentMethod);
    this.createdOrderId = '';
    this.createdPaymentIntentId = '';
    this.createdOrderResponse = null;
    this.connectedStripeAccountId = '';
  }
}
